import React, { useState, useCallback, useEffect } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

// Import the LikertScale component
import { LikertScale } from '../components/survey/LikertScale';

// Import survey data and utilities  
import { surveyQuestions } from '../data/surveyQuestions';
import { calculateScores, calculateCompletionPercentage } from '../components/survey/scoreCalculation';
import { SurveyResultsView } from '../components/survey/SurveyResultsView';
import { surveyApi } from '../services/surveyApi';
import type { SurveyResponse } from '../components/survey/surveyTypes';
import type { CategoryScore } from '../types/survey-student';

// Survey configuration
const SURVEY_CONSTANTS = {
  TOTAL_QUESTIONS: 114,
  BACKGROUND_PAGE_INDEX: -1,
  TIME_ALLOCATION_START: 123,
  TIME_ALLOCATION_END: 134
};

// Intro steps for survey introduction
const introSteps = [
  {
    title: "Welcome to Your Learning Journey!",
    description: "We're about to create your unique Learning Color Aura that will enhance your educational experience.",
    icon: "🎓"
  },
  {
    title: "Discover Your Learning Color Aura",
    description: "Through a comprehensive survey, we'll map your learning characteristics across multiple dimensions to create your personalized Learning Color Aura pattern.",
    icon: "✨"
  },
  {
    title: "Connect with Your Classmates",
    description: "Find study partners with complementary Learning Color Auras and similar learning styles for optimal collaboration and growth.",
    icon: "🤝"
  },
  {
    title: "Personalized Learning Experience", 
    description: "Help your professors understand your unique Learning Color Aura to optimize your educational journey.",
    icon: "🎯"
  },
  {
    title: "Ready to Begin?",
    description: "Let's start building your Learning Color Aura! This comprehensive survey will help create a more engaging and effective learning environment.",
    icon: "🚀"
  }
];

// Question mappings for different categories
const QUESTION_MAPPINGS = {
  VALUE_COMPONENT: [1, 16, 22, 24, 7, 11, 13, 30, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97, 4, 10, 17, 23, 26, 27],
  EXPECTANCY_COMPONENT: [2, 9, 18, 25, 5, 6, 12, 15, 20, 21, 29, 31],
  AFFECTIVE_COMPONENT: [3, 8, 14, 19, 28, 120, 121, 122],
  COGNITIVE_METACOGNITIVE: [39, 46, 59, 72, 32, 42, 49, 63, 51, 61, 62, 64, 69, 81, 36, 38, 47, 66, 71, 33, 41, 44, 54, 55, 56, 57, 76, 78, 79],
  RESOURCE_MANAGEMENT: [35, 43, 52, 65, 70, 73, 77, 80, 37, 48, 60, 74, 34, 45, 50, 40, 58, 68, 75, 115, 116, 117, 118, 119],
  TEST_ANXIETY: [3, 8, 14, 19, 28, 120, 121, 122]
};

/**
 * Get the current category group for a set of question IDs
 */
const getCurrentCategoryGroup = (questionIds: number[]) => {
  if (questionIds.length === 0) return null;
  
  const firstQuestionId = questionIds[0];
  
  // Value Component
  if (QUESTION_MAPPINGS.VALUE_COMPONENT.includes(firstQuestionId)) {
    return { name: 'Value Component', description: 'How you value and engage with course material' };
  }
  
  // Expectancy Component
  if (QUESTION_MAPPINGS.EXPECTANCY_COMPONENT.includes(firstQuestionId)) {
    return { name: 'Expectancy Component', description: 'Your beliefs about learning ability and success' };
  }
  
  // Affective Component
  if (QUESTION_MAPPINGS.AFFECTIVE_COMPONENT.includes(firstQuestionId)) {
    return { name: 'Affective Component', description: 'Emotional aspects of learning' };
  }
  
  // Cognitive and Metacognitive Strategies
  if (QUESTION_MAPPINGS.COGNITIVE_METACOGNITIVE.includes(firstQuestionId)) {
    return { name: 'Cognitive and Metacognitive Strategies', description: 'How you process and regulate learning' };
  }
  
  // Resource Management Strategies
  if (QUESTION_MAPPINGS.RESOURCE_MANAGEMENT.includes(firstQuestionId)) {
    return { name: 'Resource Management Strategies', description: 'How you manage learning resources and environment' };
  }
  
  // Time Allocation
  if (firstQuestionId >= SURVEY_CONSTANTS.TIME_ALLOCATION_START && firstQuestionId <= SURVEY_CONSTANTS.TIME_ALLOCATION_END) {
    return { name: 'Time Allocation', description: 'How you allocate time across academic and personal activities' };
  }
  
  return null;
};

/**
 * Group questions by section - each section gets its own page
 */
const getQuestionGroups = () => {
  const groups = [];
  const sections = new Set(surveyQuestions.map(q => q.section));
  
  for (const section of sections) {
    const sectionQuestions = surveyQuestions.filter(q => q.section === section);
    if (sectionQuestions.length > 0) {
      groups.push(sectionQuestions);
    }
  }
  
  return groups;
};

/**
 * Check if current questions contain test anxiety questions
 */
const hasTestAnxietyQuestions = (questionIds: number[]): boolean => {
  return questionIds.some(id => QUESTION_MAPPINGS.TEST_ANXIETY.includes(id));
};

// Progress Bar Component
const ProgressBar: React.FC<{ current: number; total: number }> = ({ current, total }) => {
  const percentage = (current / total) * 100;
  
  return (
    <div className="w-full bg-gray-200 rounded-full h-3 mb-6">
      <div 
        className="bg-gradient-to-r from-emerald-500 to-lime-500 h-3 rounded-full transition-all duration-300" 
        style={{ width: `${percentage}%` }}
      ></div>
      <div className="flex justify-between text-xs text-gray-600 mt-1">
        <span>Progress</span>
        <span>{current} / {total}</span>
      </div>
    </div>
  );
};

// Local storage hooks
const useLocalSurveyStorage = () => {
  const [responses, setResponses] = useState<SurveyResponse[]>([]);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('survey-responses');
    if (saved) {
      try {
        setResponses(JSON.parse(saved));
      } catch (error) {
        console.error('Failed to load saved responses:', error);
      }
    }
  }, []);

  // Save to localStorage when responses change
  useEffect(() => {
    localStorage.setItem('survey-responses', JSON.stringify(responses));
  }, [responses]);

  const updateResponse = useCallback((questionId: number, rating: number) => {
    setResponses(prev => {
      const filtered = prev.filter(r => r.questionId !== questionId);
      return [...filtered, { questionId, rating }];
    });
  }, []);

  const getResponse = useCallback((questionId: number) => {
    const response = responses.find(r => r.questionId === questionId);
    return response?.rating || 0;
  }, [responses]);

  const clearLocalData = useCallback(() => {
    setResponses([]);
    localStorage.removeItem('survey-responses');
  }, []);

  const getCompletionStats = useCallback(() => {
    const totalQuestions = SURVEY_CONSTANTS.TOTAL_QUESTIONS;
    const totalResponses = responses.length;
    const completionPercentage = (totalResponses / totalQuestions) * 100;
    
    return {
      totalResponses,
      totalQuestions,
      completionPercentage
    };
  }, [responses]);

  return {
    responses,
    updateResponse,
    getResponse,
    clearLocalData,
    getCompletionStats
  };
};

const Survey: React.FC = () => {
  // Navigation state
  const [currentPage, setCurrentPage] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [showMidpointSummary, setShowMidpointSummary] = useState(false);
  
  // Intro state
  const [showIntro, setShowIntro] = useState(true);
  const [introStep, setIntroStep] = useState(0);
  
  // API state
  const [surveyDataId, setSurveyDataId] = useState<string | null>(null);
  const [backendScores, setBackendScores] = useState<CategoryScore[] | null>(null);
  const [submissionState, setSubmissionState] = useState<'idle' | 'submitting' | 'calculating' | 'completed' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  // Storage hook
  const { responses, updateResponse, getResponse, clearLocalData, getCompletionStats } = useLocalSurveyStorage();

  // Get question groups and current questions
  const questionGroups = getQuestionGroups();
  const currentQuestions = currentPage >= 0 ? questionGroups[currentPage] || [] : [];
  const totalPages = questionGroups.length;

  // Get derived values
  const currentCategoryGroup = getCurrentCategoryGroup(currentQuestions.map(q => q.id));
  const { totalResponses: uniqueAnsweredQuestions, completionPercentage } = getCompletionStats();
  const motivationQuestions = surveyQuestions.filter(q => q.category === 'motivation').length;
  const learningQuestions = surveyQuestions.filter(q => q.category === 'learning-strategies').length;

  const handleResponse = useCallback((questionId: number, rating: number) => {
    updateResponse(questionId, rating);
    
    // Periodically save to backend (every 5 responses)
    if (responses.length % 5 === 0 && responses.length > 0) {
      saveResponsesIncremental();
    }
  }, [updateResponse, responses.length]);

  // Save responses incrementally to backend
  const saveResponsesIncremental = useCallback(async () => {
    try {
      await surveyApi.submitSurvey({ 
        responses, 
        surveyDataId: surveyDataId || undefined 
      });
    } catch (error) {
      console.warn('Failed to save responses incrementally:', error);
      // Don't show error to user for incremental saves
    }
  }, [responses, surveyDataId]);

  const handleNextPage = useCallback(() => {
    const currentQuestionIds = currentQuestions.map(q => q.id);
    
    if (hasTestAnxietyQuestions(currentQuestionIds) && !showMidpointSummary) {
      setShowMidpointSummary(true);
      return;
    }
    
    if (currentPage < totalPages - 2) {
      setCurrentPage((p) => p + 1);
    } else {
      // Before showing summary, submit final responses and get backend scores
      handleFinalSubmission();
    }
  }, [currentQuestions, showMidpointSummary, currentPage, totalPages]);

  const handleSkipSection = useCallback(() => {
    const currentSection = currentQuestions[0]?.section;
    
    for (let i = 0; i < questionGroups.length; i++) {
      const pageQuestions = questionGroups[i];
      if (pageQuestions.length > 0 && pageQuestions[0]?.section !== currentSection) {
        setCurrentPage(i);
        return;
      }
    }
    
    setShowSummary(true);
  }, [currentQuestions, questionGroups]);

  const handleContinueFromMidpoint = useCallback((skipCognitive = false) => {
    setShowMidpointSummary(false);
    
    if (skipCognitive) {
      // Skip to Resource Management Strategies
      const resourceManagementQuestionIds = [
        35, 43, 52, 65, 70, 73, 77, 80, 37, 48, 60, 74, 34, 45, 50, 40, 58, 68, 75
      ];
      
      for (let i = 0; i < questionGroups.length; i++) {
        const pageQuestions = questionGroups[i];
        const hasResourceManagementQuestions = pageQuestions.some(q => resourceManagementQuestionIds.includes(q.id));
        
        if (hasResourceManagementQuestions) {
          setCurrentPage(i);
          return;
        }
      }
    }
    
    // Find learning strategies section or continue to next page
    const learningStrategyQuestionIds = [39, 46, 59, 72, 32, 42, 49, 63, 51, 61, 62, 64, 69, 81, 36, 38, 47, 66, 71, 33, 41, 44, 54, 55, 56, 57, 76, 78, 79];
    
    for (let i = 0; i < questionGroups.length; i++) {
      const pageQuestions = questionGroups[i];
      const hasLearningStrategyQuestions = pageQuestions.some(q => learningStrategyQuestionIds.includes(q.id));
      
      if (hasLearningStrategyQuestions) {
        setCurrentPage(i);
        return;
      }
    }
    
    setCurrentPage(currentPage + 1);
  }, [questionGroups, currentPage]);

  // Handle final submission when survey is complete
  const handleFinalSubmission = useCallback(async () => {
    setSubmissionState('submitting');
    setError(null);
    
    try {
      // Submit all responses to backend
      const result = await surveyApi.submitSurvey({ 
        responses, 
        surveyDataId: surveyDataId || undefined 
      });
      
      if (result.surveyDataId) {
        setSurveyDataId(result.surveyDataId);
      }
      
      // Get calculated scores from backend
      setSubmissionState('calculating');
      const backendResults = await surveyApi.getResults();
      
      if (backendResults.hasResults) {
        setBackendScores(backendResults.categories);
      }
      
      setSubmissionState('completed');
      setShowSummary(true);
      
      // Clear local storage after successful backend submission
      clearLocalData();
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit survey';
      console.error('Final submission error:', errorMessage);
      setError(errorMessage);
      setSubmissionState('error');
      
      // Still show summary with local scores if backend fails
      setShowSummary(true);
    }
  }, [responses, surveyDataId, clearLocalData]);

  const handleIntroNext = useCallback(() => {
    if (introStep < introSteps.length - 1) {
      setIntroStep(introStep + 1);
    } else {
      setShowIntro(false);
    }
  }, [introStep]);

  const handleRestart = useCallback(() => {
    clearLocalData();
    setCurrentPage(0);
    setShowSummary(false);
    setShowMidpointSummary(false);
    setShowIntro(true);
    setIntroStep(0);
    setSurveyDataId(null);
    setBackendScores(null);
    setSubmissionState('idle');
    setError(null);
  }, [clearLocalData]);

  // Intro Screen
  if (showIntro) {
    return (
      <div className="py-12 flex items-center justify-center min-h-96">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden"
          >
            <div className="p-8">
              <motion.div
                key={introStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
                className="space-y-6"
              >
                <div className="flex justify-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="text-6xl"
                  >
                    {introSteps[introStep].icon}
                  </motion.div>
                </div>
                
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="text-3xl font-bold text-center text-gray-900"
                >
                  {introSteps[introStep].title}
                </motion.h2>
                
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="text-lg text-center text-gray-600 max-w-2xl mx-auto"
                >
                  {introSteps[introStep].description}
                </motion.p>
              </motion.div>

              <div className="mt-12 flex justify-between items-center">
                <div className="flex space-x-2">
                  {introSteps.map((_, index) => (
                    <div
                      key={index}
                      className={`h-2 w-2 rounded-full ${
                        index === introStep ? 'bg-emerald-500' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                
                <button
                  type="button"
                  onClick={handleIntroNext}
                  className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-lime-600 text-white rounded-lg hover:from-emerald-700 hover:to-lime-700 transition-all duration-200 shadow-md hover:shadow-lg font-semibold"
                >
                  {introStep === introSteps.length - 1 ? "Let's Begin!" : "Next"}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Midpoint Summary View
  if (showMidpointSummary) {
    const scores = calculateScores(responses);
    const motivationScores = scores.filter(score => 
      ['Intrinsic Goal Orientation', 'Extrinsic Goal Motivation', 'Task Value', 
       'Control of Learning Beliefs', 'Self-Efficacy for Learning and Performance', 'Test Anxiety'].includes(score.name)
    );

    // Color palette for different categories
    const categoryColors: Record<string, string> = {
      'Intrinsic Goal Orientation': '#3b82f6', // Blue
      'Extrinsic Goal Motivation': '#8b5cf6', // Purple
      'Task Value': '#06b6d4', // Cyan
      'Control of Learning Beliefs': '#10b981', // Emerald
      'Self-Efficacy for Learning and Performance': '#f59e0b', // Amber
      'Test Anxiety': '#ef4444', // Red
    };


    const upcomingSections = [
      {
        name: 'Cognitive and Metacognitive Strategies',
        description: 'How you process and regulate learning',
        categories: ['Rehearsal', 'Organization', 'Elaboration', 'Critical Thinking', 'Metacognitive Self-Regulation']
      },
      {
        name: 'Resource Management Strategies',
        description: 'How you manage learning resources and environment',
        categories: ['Time and Study Environment', 'Effort Regulation', 'Peer Learning', 'Help Seeking']
      }
    ];

    // Mini Ring Chart Component
    const MiniRingChart: React.FC<{ score: number; maxScore: number; color: string; size?: number }> = ({ score, maxScore, color, size = 60 }) => {
      const percentage = (score / maxScore) * 100;
      const radius = (size - 12) / 2;
      const circumference = 2 * Math.PI * radius;
      const strokeDasharray = circumference;
      const strokeDashoffset = circumference - (percentage / 100) * circumference;

      return (
        <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
          <svg width={size} height={size} className="transform -rotate-90">
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke="#e5e7eb"
              strokeWidth="6"
              fill="transparent"
              strokeLinecap="round"
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={color}
              strokeWidth="6"
              fill="transparent"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="transition-all duration-1000 ease-out"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-sm font-bold text-gray-900">{score}</span>
          </div>
        </div>
      );
    };

    return (
      <div>
        <div className="max-w-6xl mx-auto">
          <div className="space-y-8">
            {/* Congratulations Header */}
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 text-green-500">🎉</div>
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Halfway There! 🎉</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Congratulations! You've completed the first half of your learning profile. 
                Here's what we've discovered about your motivational characteristics so far.
              </p>
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
                <p className="text-sm font-medium text-gray-700">
                  ✅ Completed: Motivation & Emotional Factors • 🔄 Next: Learning Strategies & Resource Management
                </p>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="bg-white rounded-lg shadow-lg border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Your Progress</h3>
                <span className="text-sm font-medium text-green-600 bg-green-100 px-3 py-1 rounded-full">
                  50% Complete
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                <div className="bg-gradient-to-r from-green-500 to-blue-500 h-3 rounded-full transition-all duration-1000" style={{ width: '50%' }}></div>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Motivation & Emotions</span>
                <span>Learning Strategies</span>
              </div>
            </div>

            {/* Completed Characteristics */}
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">Your Motivational Profile</h3>
                <p className="text-gray-600">
                  Here are the characteristics we've recorded about how you approach learning
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {motivationScores.map((category) => {
                  const color = categoryColors[category.name] || '#6b7280';
                  
                  return (
                    <div 
                      key={category.name} 
                      className="bg-white rounded-lg shadow-md border border-gray-100 p-6 hover:shadow-lg transition-shadow"
                    >
                      <div className="flex flex-col items-center text-center space-y-4">
                        <MiniRingChart
                          score={category.score}
                          maxScore={category.maxPossible}
                          color={color}
                          size={80}
                        />
                        
                        <div className="space-y-2">
                          <h4 className="text-sm font-semibold text-gray-900 leading-tight">
                            {category.name}
                          </h4>
                          <p className="text-xs text-gray-600 leading-relaxed">{category.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* What's Coming Next */}
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">What's Coming Next</h3>
                <p className="text-gray-600">
                  In the second half, we'll explore your learning strategies and how you manage resources
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {upcomingSections.map((section) => (
                  <div 
                    key={section.name}
                    className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-6"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          ⏰
                        </div>
                      </div>
                      
                      <div className="flex-1 space-y-3">
                        <h4 className="text-lg font-semibold text-gray-900">{section.name}</h4>
                        <p className="text-sm text-gray-600">{section.description}</p>
                        
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-gray-700">You'll discover:</p>
                          <ul className="text-xs text-gray-600 space-y-1">
                            {section.categories.slice(0, 3).map((category) => (
                              <li key={category} className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                                {category}
                              </li>
                            ))}
                            {section.categories.length > 3 && (
                              <li className="text-blue-600 font-medium">
                                +{section.categories.length - 3} more characteristics
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Choose Your Path */}
            <div className="text-center">
              <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Choose Your Path</h4>
                <p className="text-sm text-gray-600 mb-4">
                  You can complete the full assessment or skip the detailed cognitive strategies section if you prefer a shorter experience.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Skip Option */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200 hover:border-green-300 transition-colors">
                    <h5 className="font-semibold text-gray-900 mb-2">Skip to Resource Management</h5>
                    <p className="text-xs text-gray-600 mb-3">
                      Skip cognitive strategies and go directly to resource management and time allocation questions (8-10 more questions)
                    </p>
                    <button
                      type="button"
                      onClick={() => handleContinueFromMidpoint(true)}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-semibold"
                    >
                      Skip to Resource Management
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Complete Assessment Option */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition-colors">
                    <h5 className="font-semibold text-gray-900 mb-2">Complete Full Assessment</h5>
                    <p className="text-xs text-gray-600 mb-3">
                      Continue with all cognitive and metacognitive strategy questions for a comprehensive learning profile (25-30 more questions)
                    </p>
                    <button
                      type="button"
                      onClick={() => handleContinueFromMidpoint(false)}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-semibold"
                    >
                      Continue Full Assessment
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading states during submission
  if (submissionState === 'submitting') {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Submitting Your Survey</h2>
          <p className="text-gray-600">Please wait while we save your responses...</p>
        </div>
      </div>
    );
  }

  if (submissionState === 'calculating') {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Calculating Your Scores</h2>
          <p className="text-gray-600">Generating your learning profile analysis...</p>
          <p className="text-sm text-gray-500 mt-2">This may take a few moments</p>
        </div>
      </div>
    );
  }

  // Final Summary View
  if (showSummary) {
    // Use backend scores if available, otherwise calculate locally
    const scores = backendScores || calculateScores(responses);
    const completionPercentage = calculateCompletionPercentage(responses);

    return (
      <SurveyResultsView
        scores={scores}
        totalResponses={responses.length}
        completionPercentage={completionPercentage}
        submissionState={submissionState}
        error={error}
        onBack={() => setShowSummary(false)}
        onRestart={handleRestart}
      />
    );
  }

  // Main Survey Interface
  return (
    <div>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Learning Motivation & Strategy Survey</h1>
          <button
            type="button"
            onClick={handleRestart}
            className="text-sm text-emerald-600 hover:text-emerald-800 underline"
          >
            Clear Form & Restart
          </button>
        </div>

        {/* Instructions */}
        {currentPage >= 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Survey Instructions</h3>
              <p className="text-gray-600">Rate each statement from 1 (not at all true of me) to 7 (very true of me)</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  <span className="text-gray-700">Part A: Motivation ({motivationQuestions} questions)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-lime-500 rounded-full"></div>
                  <span className="text-gray-700">Part B: Learning Strategies ({learningQuestions} questions)</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Current page:</span>
                  <span className="font-medium text-gray-900">{currentPage + 1} of {totalPages - 1}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Progress:</span>
                  <span className="font-medium text-emerald-600">{uniqueAnsweredQuestions} / {surveyQuestions.length} ({completionPercentage.toFixed(1)}%)</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <ProgressBar
          current={currentPage + 2} // +2 because we start at -1
          total={totalPages}
        />

        {/* Category Information */}
        {currentCategoryGroup && (
          <div className="mb-8 p-6 bg-gradient-to-r from-emerald-50 to-lime-50 rounded-lg border-l-4 border-emerald-500">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {currentCategoryGroup.name}
            </h2>
            <p className="text-gray-700 text-sm">
              {currentCategoryGroup.description}
            </p>
            <div className="mt-4 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Section: {currentQuestions[0]?.section} • {currentQuestions.length} questions
              </div>
              <button
                type="button"
                onClick={handleSkipSection}
                className="text-sm text-emerald-600 hover:text-emerald-800 transition-colors underline"
              >
                Skip this section →
              </button>
            </div>
          </div>
        )}

        {/* Questions */}
        {currentPage >= 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Questions ({currentQuestions.length} on this page)
              </h3>
              <div className="flex items-center gap-2">
                <div className="text-xs text-gray-500">Answered on this page:</div>
                <div className="text-xs font-semibold text-emerald-600">
                  {currentQuestions.filter(q => getResponse(q.id) > 0).length} / {currentQuestions.length}
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              {currentQuestions.map((question) => {
                const isAnswered = getResponse(question.id) > 0;
                return (
                  <div key={question.id} className={`border-b border-gray-200 pb-6 ${isAnswered ? 'bg-emerald-50 bg-opacity-30' : ''} rounded-lg p-4`}>
                    <div className="flex items-start">
                      <div className="flex items-center gap-2 mr-4 mt-1 flex-shrink-0">
                        <span className="text-sm font-medium text-gray-500">
                          {question.id}.
                        </span>
                        {isAnswered && (
                          <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-4">
                          <p className="text-gray-900 font-medium">{question.text}</p>
                          <span className="text-xs text-gray-500 ml-4">
                            {question.category === 'motivation' ? 'Part A' : 'Part B'}
                          </span>
                        </div>
                        <LikertScale
                          value={getResponse(question.id)}
                          onChange={(rating) => handleResponse(question.id, rating)}
                          type={question.questionType || 'standard'}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <button
            type="button"
            onClick={() => setCurrentPage((p) => p - 1)}
            disabled={currentPage === 0}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </button>

          <button
            type="button"
            onClick={handleNextPage}
            className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
              currentPage === totalPages - 2
                ? 'bg-gradient-to-r from-emerald-600 to-lime-600 text-white hover:from-emerald-700 hover:to-lime-700 shadow-md hover:shadow-lg'
                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {currentPage === totalPages - 2 ? 'View Results' : 'Next'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        </div>

        {/* Completion message */}
        {currentPage === totalPages - 2 && completionPercentage < 100 && (
          <div className="bg-gradient-to-r from-emerald-50 to-lime-50 rounded-lg p-4 mt-6 border border-emerald-200">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                <span className="text-sm font-semibold text-emerald-800">Survey Progress</span>
              </div>
              <p className="text-sm text-gray-700 mb-2">
                You've answered <span className="font-semibold text-emerald-600">{uniqueAnsweredQuestions}</span> out of <span className="font-semibold">{surveyQuestions.length}</span> questions
              </p>
              <p className="text-xs text-gray-600">
                You can view results now or continue answering more questions to get a more complete profile.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Survey;